// FRU-21694 — Direct-to-Hope product-presentation resolver.
// TWO orthogonal dimensions:
//   BASE PRODUCT = home equity | HELOC | cash-out | home improvement | refinance | purchase (families the form collects)
//   PROGRAM      = VA | FHA | Jumbo | (none) — a loan program a person may be INTERESTED in; an ad click
//                  does NOT prove eligibility (abx). Programs layer onto a base; kept as neutral interest/
//                  context until the deterministic form questions validate.
//
// COPY RULE (Ed 2026-07-13) — phrase equity as an ACTION, never a manufactured product noun:
//   • home equity / cash-out = "getting cash" -> ACTION framing: "Get Home Equity", "Get Cash Out". With a
//     program it reads "Get Home Equity with a VA Loan" (FHA / Jumbo the same).
//   • HELOC is NEVER tied to a program (no "VA HELOC" / "FHA HELOC" / "Jumbo HELOC"). It uses the HELOC +
//     line-of-credit keywords phrased as a COMPARISON, like FRU's ads ("Compare HELOCs & Home Equity Lines
//     of Credit"). A program on a HELOC entry stays Hope context only, never in the copy.
//   • Program PREFIX applies ONLY to the real program products: Purchase & Refinance ("VA Refinance Rates").
//   • Home Improvement keeps its OWN label (never collapsed to Home Equity).
//   • Bare program, no base -> "VA Loan Rates" and the form ASKS the loan type (no purchase default).
// No rates/APR, no eligibility/benefit claims, no "qualify" copy; never build copy from raw
// keyword/adgroup/campaign; safe generic fallback. Base resolution mirrors the chat recognize-skip
// cascade (type + keyword) so the two never diverge.

export interface ProductPresentation {
  productKey: string;      // homeequity|heloc|cashout|homeimprovement|refinance|purchase|generic
  program: string;         // 'va' | 'fha' | 'jumbo' | '' — INTEREST from the ad, not verified eligibility
  title: string;
  description: string;
  h1: string;
  h2: string;   // the action line (rates + options + payment)
  h3: string;   // the value line (compare offers, no obligation)
  introHtml: string;   // Hope's ONE conversational acknowledgement w/ inline <h1>/<h2>/<h3> (the visible intro)
  hopeContext: string;
  loanPurpose: string;     // BASE only ('' when unknown -> form asks)
  formType: string;        // BASE only ('' when none)
  lpButton: string;        // BASE only (never 'ai', never a program)
}

const PROGRAM_LABEL: { [k: string]: string } = { va: 'VA', fha: 'FHA', jumbo: 'Jumbo' };
const PROGRAM_WITH: { [k: string]: string } = { va: 'a VA Loan', fha: 'an FHA Loan', jumbo: 'a Jumbo Loan' };
// Neutral, compliance-safe context for Hope (internal). Program INTEREST only — no benefit claims, no
// asserted eligibility; the deterministic questions confirm it. (abx review of c4dc664a.)
const PROGRAM_INTEREST: { [k: string]: string } = {
  va: 'showed interest in a VA loan (veteran / active military); do NOT assert VA eligibility or benefits — confirm via the veteran/military question',
  fha: 'showed interest in an FHA loan; do NOT assert FHA eligibility or benefits — confirm via the form answers',
  jumbo: 'showed interest in a jumbo loan (a larger loan amount); confirm via the property value / loan amount',
};

// programStyle drives how a program composes into the copy:
//   'prefix' — real program product; prefix the noun ("VA Refinance Rates")
//   'action' — getting cash; action verb + "with a VA Loan" ("Get Home Equity with a VA Loan")
//   'none'   — HELOC; NEVER tie a program; comparison copy only, program is Hope context only
//   'label'  — keep the base label; program stays context only
interface Base {
  key: string;
  programStyle: 'prefix' | 'action' | 'none' | 'label';
  h1: string;            // base-only H1 (no program)
  prefixNoun: string;    // 'prefix' style: composed as "{Program} {prefixNoun}"
  desc: string;
  hope: string;
  loanPurpose: string; formType: string; lpButton: string;
}
const BASES: { [k: string]: Base } = {
  homeequity:      { key: 'homeequity',      programStyle: 'action', h1: 'Get Home Equity',                               prefixNoun: '',                   desc: 'Compare home equity options from multiple lenders and turn your home equity into cash.',  hope: 'wants to get cash from their home equity',                     loanPurpose: 'refinance', formType: 'heloc',          lpButton: 'homeequity' },
  cashout:         { key: 'cashout',         programStyle: 'action', h1: 'Get Cash Out',                                  prefixNoun: '',                   desc: 'Compare cash-out refinance options from multiple lenders and take cash out of your home.', hope: 'wants to take cash out of their home (cash-out refinance)',     loanPurpose: 'refinance', formType: 'cashout',        lpButton: 'cashout' },
  heloc:           { key: 'heloc',           programStyle: 'none',   h1: 'Compare HELOCs & Home Equity Lines of Credit',  prefixNoun: '',                   desc: 'Compare HELOC and home equity line of credit rates and options from multiple lenders.',    hope: 'wants a HELOC / home equity line of credit',                   loanPurpose: 'refinance', formType: 'heloc',          lpButton: 'heloc' },
  homeimprovement: { key: 'homeimprovement', programStyle: 'label',  h1: 'Home Improvement Loan Rates',                   prefixNoun: '',                   desc: 'Compare home improvement loan options from multiple lenders.',                             hope: 'wants funds for home improvements / renovations',              loanPurpose: 'refinance', formType: 'homeimprovement', lpButton: 'homeimprovement' },
  refinance:       { key: 'refinance',       programStyle: 'prefix', h1: 'Mortgage Refinance Rates',                      prefixNoun: 'Refinance Rates',    desc: 'Compare mortgage refinance rates and options from multiple lenders.',                      hope: 'wants to refinance their existing mortgage (rate/payment/term)', loanPurpose: 'refinance', formType: '',              lpButton: 'refinance' },
  purchase:        { key: 'purchase',        programStyle: 'prefix', h1: 'Home Purchase Rates',                           prefixNoun: 'Home Purchase Rates', desc: 'Compare home purchase mortgage rates and options from multiple lenders.',                  hope: 'is buying a home',                                             loanPurpose: 'purchase',  formType: '',              lpButton: 'purchase' },
};

function norm(v: any): string { return (v || '').toString().toLowerCase().trim(); }

export function resolveProgram(type: any, keyword: any): string {
  const s = norm(type) + ' ' + norm(keyword);
  if (/\bva\b|va-(loan|mortgage|home|refi|purchase|heloc|equity)/.test(s)) { return 'va'; }
  if (/\bfha\b|fha-(loan|mortgage|home|refi|purchase|streamline)/.test(s)) { return 'fha'; }
  if (/\bjumbo\b/.test(s)) { return 'jumbo'; }
  return '';
}

// FRU-21801 (Ed 2026-07-22): rate-shopping SURFACE intents (calculator, today's rates) that carry NO base
// product -- the visitor has not told us purchase vs refi vs equity, so we give an aligned title + intro
// but leave the base UNKNOWN (the form still asks the loan type; no tile pre-highlight). Only consulted
// when no explicit base and no program fired.
export function resolveIntent(type: any, keyword: any): string {
  const s = norm(type) + ' ' + norm(keyword);
  if (/calculat|payment calculator/.test(s)) { return 'calculator'; }   // abx 7/22: require calculat / "payment calculator", not bare "payment" (over-matched refinance "lower my payment")
  if (/\btoday\b|rates today|current rate/.test(s)) { return 'today'; }
  return '';
}

// Collect the ad keyword from the several params Google may send, DE-DUPLICATING identical values (Google
// routinely sends utm_keyword AND keyword with the SAME value -> "mortgage calculator mortgage calculator"
// doubling in the logs + presentation) and DROPPING a purely numeric / punctuation "keyword" (garbage bid
// token) so it never pollutes the title, intro, or the logged presentation. Any letter keeps the value
// (e.g. "203k", "15 year fixed").
export function collectKeywords(...vals: any[]): string {
  const seen: { [k: string]: boolean } = {};
  const out: string[] = [];
  for (const v of vals) {
    const val = (v || '').toString().trim();
    if (!val) { continue; }
    const low = val.toLowerCase();
    if (seen[low]) { continue; }
    if (!/[a-z]/.test(low)) { continue; }   // no letters -> numeric / punctuation garbage
    seen[low] = true;
    out.push(val);
  }
  return out.join(' ');
}

export interface KeywordParamResolution {
  keyword: string;
  all: string;
  productKey: string;
  productSource: 'route' | 'keyword' | 'campaign' | '';
}

/**
 * Resolve the complete presentation from one URL contract.
 *
 * Keep every consumer-facing reader (page title/meta and Hope's opening) on the
 * same selected keyword and product. Callers that concatenate the four keyword
 * parameters independently can reintroduce the FRU-22465 split where metadata
 * names one product while the reviewed offer and questionnaire use another.
 */
export function resolveProductPresentationFromParams(
  qp: any,
  prettyDomain?: any,
): ProductPresentation {
  const keywordResolution = resolveKeywordParams(qp);

  return resolveProductPresentation(
    qp && typeof qp.get === 'function'
      ? qp.get('LoanPurpose') || qp.get('loanPurpose') || qp.get('loanpurpose')
      : '',
    qp && typeof qp.get === 'function'
      ? qp.get('FormType') || qp.get('formType')
      : '',
    keywordResolution.productKey || (qp && typeof qp.get === 'function' ? qp.get('lp_button') : ''),
    qp && typeof qp.get === 'function' ? qp.get('type') : '',
    keywordResolution.keyword,
    prettyDomain,
  );
}

function productFamily(key: string): string {
  return key === 'heloc' || key === 'homeequity' ? 'equity' : key;
}

// FRU-22465: the opener and product resolver must select one consumer intent from the same URL
// contract. Combining conflicting params lets regex family precedence choose a different product
// than the opener's first exact keyword match. Keep the established opener order, choose the first
// recognized product/program/surface intent, and retain the full de-duplicated set for telemetry only.
export function resolveKeywordParams(qp: any): KeywordParamResolution {
  if (!qp || typeof qp.get !== 'function') {
    return { keyword: '', all: '', productKey: '', productSource: '' };
  }

  const values = ['keyword', 'utm_keyword', 'utm_term', 'query'].map((name) => qp.get(name));
  const all = collectKeywords(...values);
  let fallback = '';
  let selected = '';
  const explicitBase = resolveBase(
    qp.get('LoanPurpose') || qp.get('loanPurpose') || qp.get('loanpurpose'),
    qp.get('FormType') || qp.get('formType'),
    qp.get('lp_button'),
    qp.get('type'),
    '',
  );

  for (const raw of values) {
    const keyword = collectKeywords(raw);
    if (!keyword) { continue; }
    const keywordBase = resolveBase('', '', '', '', keyword);
    const program = resolveProgram('', keyword);
    const intent = resolveIntent('', keyword);

    // Route/form product signals own the questionnaire and rate bucket. A conflicting ad parameter
    // cannot own the conversational lead while the explicit route owns the offer. Keep looking for
    // a later keyword that matches the route; an unrelated brand phrase remains a safe fallback.
    if (
      explicitBase.explicit
      && keywordBase.explicit
      && productFamily(keywordBase.key) !== productFamily(explicitBase.key)
    ) {
      continue;
    }

    if (!fallback && !keywordBase.explicit && !program && !intent) { fallback = keyword; }

    if (
      keywordBase.explicit
      || !!program
      || !!intent
    ) {
      selected = keyword;
      break;
    }
  }

  if (!selected) { selected = fallback; }

  const selectedBase = resolveBase(
    qp.get('LoanPurpose') || qp.get('loanPurpose') || qp.get('loanpurpose'),
    qp.get('FormType') || qp.get('formType'),
    qp.get('lp_button'),
    qp.get('type'),
    selected,
  );
  if (selectedBase.explicit) {
    return {
      keyword: selected,
      all,
      productKey: selectedBase.key,
      productSource: explicitBase.explicit ? 'route' : 'keyword',
    };
  }

  // A recognized program or rate-shopping surface is itself the chosen intent. It intentionally
  // leaves the base unknown so the form asks the loan type; an internal campaign name cannot turn a
  // VA-loan or calculator opening into a silently preselected product.
  if (selected && (resolveProgram('', selected) || resolveIntent('', selected))) {
    return { keyword: selected, all, productKey: '', productSource: 'keyword' };
  }

  // Preserve the existing direct-ad fallback for unambiguous campaign names, but make it part of
  // this one resolution so the opener/H1/rate and questionnaire cannot silently choose separately.
  // Refinance remains excluded: campaign names are our labels, not proof the searcher wants refi.
  const campaign = norm(qp.get('utm_campaign'));
  let campaignProduct = '';
  if (/purchase|buying|buy a home|new home|first.?time/.test(campaign)) {
    campaignProduct = 'purchase';
  } else if (/cash.?out/.test(campaign)) {
    campaignProduct = 'cashout';
  } else if (/home.?improv|renovat|remodel/.test(campaign)) {
    campaignProduct = 'homeimprovement';
  } else if (/heloc|line[- ]of[- ]credit|equity.?line/.test(campaign)) {
    campaignProduct = 'heloc';
  } else if (/home.?equity|2nd mortgage|second mortgage/.test(campaign)) {
    campaignProduct = 'homeequity';
  }

  return {
    keyword: selected,
    all,
    productKey: campaignProduct,
    productSource: campaignProduct ? 'campaign' : '',
  };
}

// BASE product — mirrors the chat recognize-skip cascade (type + keyword) so the two never diverge.
// explicit=false => no real base signal (only a program) -> the form asks the loan type.
export function resolveBase(loanPurpose: any, formType: any, urlLpButton: any, type: any, keyword?: any): { key: string, explicit: boolean } {
  const lp = norm(urlLpButton);
  if (['homeequity', 'heloc', 'cashout', 'homeimprovement', 'refinance', 'purchase'].indexOf(lp) >= 0) { return { key: lp, explicit: true }; }
  const kw = (norm(type) + ' ' + norm(keyword)).trim();
  const t = norm(type);
  if (/purchase|buying|buy.a.home|new.home|first.?time/.test(kw)) { return { key: 'purchase', explicit: true }; }
  if (/cash.?out/.test(kw)) { return { key: 'cashout', explicit: true }; }
  if (/home.?improv|renovat|remodel/.test(kw)) { return { key: 'homeimprovement', explicit: true }; }
  if (/home.?equity|heloc|line[- ]of[- ]credit|equity.?line|2nd mortgage|second mortgage/.test(kw) || /home-equity-loan|heloc|equity-calculator|line-of-credit/.test(t)) {
    // Ed 8/5: "equity line" is a HELOC too (line-of-credit family).
    return { key: /heloc|line[- ]of[- ]credit|equity.?line/.test(kw) ? 'heloc' : 'homeequity', explicit: true };
  }
  // FRU-21801 (Ed 7/21): refinance is NEVER inferred from generic rate words ("mortgage rates",
  // "lower rate") — only ~30% of generic-rate searchers want refinance; the old pattern presented a
  // refinance intro/H1 (and, mirrored in the chat cascade, force-seeded the flow) to ~70% of generic
  // direct-Hope traffic. Refinance requires the searcher's own keyword to say refinance/refi, or an
  // explicitly-refinance type param. Callers pass typed search words only (utm_keyword/utm_term/query).
  // 2026-09-26: 'refinanc' so "refinancing calculator" / "refinanced" count (the old /refinance/ missed them and those
  // searchers were asked the loan type). A streamline is a refinance only as an FHA or VA streamline, so it needs FHA/VA
  // in the keyword or the route type; a bare "streamline mortgage application" says nothing about purchase vs refinance.
  const kwn = norm(keyword);
  const fhaVaStreamline = /\bstreamline\b/.test(kwn) && (/\b(fha|va)\b/.test(kwn) || /\b(fha|va)\b/.test(t));
  if (/refinanc|\brefi\b/.test(kwn) || fhaVaStreamline || /^refinance(-calculator)?$|^refi$/.test(t)) { return { key: 'refinance', explicit: true }; }
  const ft = norm(formType);
  if (ft === 'heloc') { return { key: 'homeequity', explicit: true }; }
  if (ft === 'cashout') { return { key: 'cashout', explicit: true }; }
  if (ft === 'homeimprovement') { return { key: 'homeimprovement', explicit: true }; }
  const base = norm(loanPurpose);
  if (base === 'purchase') { return { key: 'purchase', explicit: true }; }
  if (base === 'refinance') { return { key: 'refinance', explicit: true }; }
  return { key: '', explicit: false };
}

// Compose the program into the base H1 per the COPY RULE. HELOC ('none') and 'label' bases NEVER carry a
// program in the copy.
function composeH1(base: Base, program: string): string {
  if (!program || base.programStyle === 'none' || base.programStyle === 'label') { return base.h1; }
  if (base.programStyle === 'prefix') { return `${PROGRAM_LABEL[program]} ${base.prefixNoun}`; }
  return `${base.h1} with ${PROGRAM_WITH[program]}`;   // 'action' -> "Get Home Equity with a VA Loan"
}

// FRU-21694 (Ed 2026-07-13): the intro heading hierarchy rendered as real <h1>/<h2>/<h3> in Hope's
// opening bubble -- so AdsBot + crawlers see the product relevance. There is ALWAYS a set (product-
// specific, or a sensible DEFAULT for a no-signal direct entry); the CALLER gates rendering to a real
// direct-Hope entry (startAssistant=1) so a normal / control page is never touched.
const H3_VALUE = 'Compare real offers with no obligation.';   // Ed 7/14: no dash, no lender mention (keep them moving)
const H3_HTML = `<h3>${H3_VALUE}</h3>`;
// Ed 7/14: the intro embeds this mid-sentence ("...payment, and compare real offers"), so the
// leading letter is lower-case there; the standalone <h3> keeps H3_VALUE's capitalized form.
const H3_HTML_LC = `<h3>${H3_VALUE.charAt(0).toLowerCase()}${H3_VALUE.slice(1)}</h3>`;
const H2_NOUN: { [k: string]: string } = {
  homeequity: 'home equity', cashout: 'cash-out', heloc: 'HELOC',
  homeimprovement: 'home improvement', refinance: 'refinance', purchase: 'home purchase',
};
function usesProgramCopy(baseKey: string): boolean {
  return baseKey !== 'heloc' && baseKey !== 'homeimprovement';
}
function actionH2(baseKey: string, program: string): string {
  const label = (program && usesProgramCopy(baseKey)) ? PROGRAM_LABEL[program] : (H2_NOUN[baseKey] || 'mortgage');
  return `Let's get your ${label} rates and options and calculate your payment.`;
}

// FRU-21694 (Ed 2026-07-13): Hope's ONE conversational acknowledgement. The product/action/value ride as
// INLINE <h1>/<h2>/<h3> (styled as normal chat text) so the visitor sees Hope talking, AdsBot/crawlers
// see the heading hierarchy. The FOLLOWING question is rendered by the component as plain text (never a
// heading). Grammar is controlled here per product so every base+program reads naturally.
const PROGRAM_WITH_L: { [k: string]: string } = { va: 'a VA loan', fha: 'an FHA loan', jumbo: 'a jumbo loan' };
function h1Clause(baseKey: string, program: string): string {
  const w = (program && usesProgramCopy(baseKey)) ? ' with ' + PROGRAM_WITH_L[program] : '';
  switch (baseKey) {
    case 'homeequity': return 'get home equity' + w;
    case 'cashout': return 'get cash out of your home' + w;
    case 'heloc': return 'compare HELOCs and home equity lines of credit';
    case 'homeimprovement': return 'fund your home improvements';
    case 'refinance': return w ? 'refinance' + w : 'refinance your mortgage';
    case 'purchase': return 'buy your home' + w;
    default: return 'find your best mortgage';
  }
}
function introHtmlFor(baseKey: string, program: string): string {
  // Ed 7/13 PM: ONE sentence, zero repetition ("rates" once, product once, payment once). The next
  // bubble asks the question; the intro never restates what it says.
  if (baseKey === 'heloc') {
    return `Let's get your best <h1><strong>HELOC and home equity line of credit</strong></h1> rates and options, <h2>calculate your payment</h2>, and ${H3_HTML_LC}`;
  }
  if (baseKey === 'default' || !H2_NOUN[baseKey]) {
    return `Let's get your best <h1><strong>mortgage rates and options</strong></h1>, <h2>calculate your payment</h2>, and ${H3_HTML_LC}`;
  }
  const clause = h1Clause(baseKey, program);
  return `Let's get your best rates and options to <h1><strong>${clause}</strong></h1>, <h2>calculate your payment</h2>, and ${H3_HTML_LC}`;
}

export function resolveProductPresentation(loanPurpose: any, formType: any, urlLpButton: any, type: any, keyword?: any, prettyDomain?: any): ProductPresentation {
  const brand = (prettyDomain || 'FreeRateUpdate').toString();
  const program = resolveProgram(type, keyword);
  const b = resolveBase(loanPurpose, formType, urlLpButton, type, keyword);

  // Program named but no explicit base (bare "VA loan" ad) -> program-generic; DO NOT default the base to
  // purchase (abx) -- leave it unknown so the flow asks the loan type.
  if (program && !b.explicit) {
    const label = PROGRAM_LABEL[program];
    return {
      productKey: 'generic', program,
      title: `${label} Loan Rates | ${brand}`,
      description: `Compare ${label} loan rates and options from multiple lenders.`,
      h1: `${label} Loan Rates`,
      h2: `Let's get your ${label} rates and options and calculate your payment.`,
      h3: H3_VALUE,
      // Ed 7/28: copy restored; the FE folds it into the first question bubble (see fold below) so
      // there is ONE opening bubble without losing the program acknowledgement.
      introHtml: `Let's get your best <h1><strong>${label} loan rates and options</strong></h1>, <h2>calculate your payment</h2>, and ${H3_HTML_LC}`,
      hopeContext: `Arrived from a ${label} loan ad — ${PROGRAM_INTEREST[program]}.`,
      loanPurpose: '', formType: '', lpButton: '',
    };
  }

  if (!b.explicit) {
    // FRU-21801 (Ed 2026-07-22): a rate-shopping surface (calculator / today's rates) with no base ->
    // an aligned title + intro, base still UNKNOWN so the form asks the loan type (never a pre-highlighted
    // tile). Direct-Hope only (the caller gates on startAssistant=1).
    const intent = resolveIntent(type, keyword);
    if (intent === 'calculator') {
      return {
        productKey: 'calculator', program: '',
        title: `Mortgage Payment Calculator | ${brand}`,
        description: `Calculate your mortgage payment and compare today's best rates and options from top lenders, no obligation.`,
        h1: `Mortgage Payment Calculator`,
        h2: `Let's calculate your payment and get your best rates and options.`,
        h3: H3_VALUE,
        introHtml: '',   // Ed 7/24: rate-focused pages get NO intro bubble -- the rate sentence in the loan-type step IS the intro (double-message kill); title/meta keep the aligned copy
        hopeContext: `Arrived from a mortgage payment calculator ad${(keyword || '').toString().trim() ? ` (searched "${(keyword || '').toString().trim().slice(0, 80)}")` : ''} \u2014 wants to calculate a payment and compare rates; ask the loan type.`,
        loanPurpose: '', formType: '', lpButton: '',
      };
    }
    if (intent === 'today') {
      return {
        productKey: 'today', program: '',
        title: `Today's Mortgage Rates & Options | ${brand}`,
        description: `Compare today's mortgage rates and options from multiple lenders, no obligation.`,
        h1: `Today's Mortgage Rates & Options`,
        h2: `Let's get today's rates and options and calculate your payment.`,
        h3: H3_VALUE,
        introHtml: '',   // Ed 7/24: same -- no intro bubble on today's-rates entries
        hopeContext: `Arrived from a today's-rates ad${(keyword || '').toString().trim() ? ` (searched "${(keyword || '').toString().trim().slice(0, 80)}")` : ''} \u2014 ask the loan type.`,
        loanPurpose: '', formType: '', lpButton: '',
      };
    }
    // No product signal fired. Give the direct-Hope intro a sensible, relevant DEFAULT heading set (Ed
    // 2026-07-13) so AdsBot / crawlers always see a real, on-topic H1/H2/H3 -- never empty. The CALLER
    // gates rendering to a real direct-Hope entry (startAssistant=1), so a normal / control page is
    // unchanged (this default never leaks there).
    // Ed 7/28: on an unmapped keyword ("credit union mortgages", "suntrust mortgage") we correctly
    // refuse to GUESS a product -- but we were also throwing the consumer's own search term away, so
    // Hope opened completely generic and never acknowledged what they searched for. Carry the keyword
    // through as CONTEXT ONLY: the brain can speak to it naturally, while the base stays unknown so
    // the form still asks the loan type and no tile is pre-highlighted (FRU-21801 stands).
    const _kw = (keyword || '').toString().trim().slice(0, 80);
    return {
      productKey: 'generic', program: '',
      title: `Compare Mortgage Rates & Options | ${brand}`,
      description: 'Compare mortgage rates and options from multiple lenders \u2014 no obligation.',
      h1: 'Compare Mortgage Rates & Options',
      h2: `Let's get your rate, options, and payment.`,
      h3: H3_VALUE,
      introHtml: '',   // Ed 7/24: same -- no intro bubble on the generic rates entry
      hopeContext: _kw
        ? `Arrived from a search ad for "${_kw}" \u2014 acknowledge what they searched for in your own words, do NOT assume a loan type, and ask the loan type.`
        : '',
      loanPurpose: '', formType: '', lpButton: '',
    };
  }

  const base = BASES[b.key];
  const h1 = composeH1(base, program);
  // hopeContext: base intent + (unless HELOC, where we stay away from program×HELOC) the neutral program
  // INTEREST, so Hope can acknowledge the program without asserting eligibility -- never in the title.
  const progNote = (program && base.programStyle !== 'none') ? ` They also ${PROGRAM_INTEREST[program]}.` : '';
  return {
    productKey: base.key, program,
    title: `${h1} | ${brand}`,
    description: base.desc,
    h1,
    h2: actionH2(base.key, program),
    h3: H3_VALUE,
    // Ed 7/24: the home-equity landing combines intro + rate + question into ONE bubble (the
    // propertyType step builds it), so its separate intro bubble is suppressed here.
    // Ed 7/28: the copy is RESTORED -- suppressing it entirely left refinance / cash-out entries with
    // no product mention at all (those bases skip the loan-type step, so the first question is
    // propertyType). The FE now FOLDS this into the first question bubble instead of rendering a
    // second bubble, which is what "one opening bubble" actually meant.
    introHtml: base.key === 'homeequity' ? '' : introHtmlFor(base.key, program),
    hopeContext: `${base.hope}.${progNote}`,
    loanPurpose: base.loanPurpose, formType: base.formType, lpButton: base.lpButton,
  };
}
